import {isFeatureEnabled} from 'feature-flags';

import {useState} from 'react';

import {fromPairs, isNil, prop} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {
  useGetUserDashboardSettingsQuery,
  usePutUserDashboardArrangementMutation,
} from '@dms/api/customer';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {DASHBOARD_TILE_CATALOGUE} from '../consts/dashboardTileCatalogue';
import {DASHBOARD_TILE_IDS} from '../consts/dashboardTileIds';
import {DashboardTileDefinition} from '../types/DashboardTileDefinition';
import {DashboardTileId} from '../types/DashboardTileId';
import {DashboardTilePlacement} from '../types/DashboardTilePlacement';
import {DashboardTileSize} from '../types/DashboardTileSize';
import {DASHBOARD_GRID_COLUMNS} from '../utils/allocateDashboardGrid';
import {buildDefaultDashboardArrangement} from '../utils/buildDefaultDashboardArrangement';
import {hideDashboardTile} from '../utils/hideDashboardTile';
import {positionVisibleDashboardTiles} from '../utils/positionVisibleDashboardTiles';
import {DashboardDropTransaction} from '../utils/projectDashboardDrop';
import {reconcileDashboardLayout} from '../utils/reconcileDashboardLayout';
import {reflowDashboardGrid} from '../utils/reflowDashboardGrid';
import {reorderDashboardGrid} from '../utils/reorderDashboardGrid';
import {selectShownDashboardTiles} from '../utils/selectShownDashboardTiles';
import {setDashboardTileSize} from '../utils/setDashboardTileSize';
import {showDashboardTile} from '../utils/showDashboardTile';
import {toDashboardTileSizeCode} from '../utils/toDashboardTileSizeCode';
import {widenUnpairedDashboardTiles} from '../utils/widenUnpairedDashboardTiles';

interface UseDashboardLayoutParams {
  definitions: DashboardTileDefinition[];
}

export function useDashboardLayout(params: UseDashboardLayoutParams) {
  const {data: userDashboardSettings} = useGetUserDashboardSettingsQuery();
  const [putUserDashboardArrangement, {isLoading: isSaving}] =
    usePutUserDashboardArrangementMutation();

  const [draftArrangement, setDraftArrangement] = useState<DashboardTilePlacement[] | null>(null);

  const isCustomisationEnabled = isFeatureEnabled(featureFlags.DASHBOARD_CUSTOMISATION);
  const storedArrangement = isCustomisationEnabled ? userDashboardSettings?.arrangement : null;

  const isEnabledById = fromPairs(
    DASHBOARD_TILE_IDS.map((id) => [
      id,
      params.definitions.some((definition) => definition.id === id && definition.isEnabled),
    ])
  );

  const allowedIds = params.definitions
    .filter((definition) => definition.isAllowed)
    .map((definition) => definition.id);

  // With customisation off the legacy settings still decide, whatever the arrangement says.
  const getVisibleIds = (placements: DashboardTilePlacement[]) =>
    allowedIds.filter((id) =>
      isCustomisationEnabled
        ? placements.some((placement) => placement.id === id && placement.isVisible)
        : prop(id, isEnabledById)
    );

  // Default sizes only: a stored arrangement keeps the sizes the user chose.
  const withDefaultSizes = (placements: DashboardTilePlacement[]) =>
    widenUnpairedDashboardTiles(placements, getVisibleIds(placements));

  const reconciledArrangement = reconcileDashboardLayout(storedArrangement, isEnabledById);
  const savedArrangement = isNil(storedArrangement)
    ? withDefaultSizes(reconciledArrangement)
    : reconciledArrangement;
  const activeArrangement = draftArrangement ?? savedArrangement;

  const visibleIds = getVisibleIds(activeArrangement);

  const shownTiles = selectShownDashboardTiles(activeArrangement, params.definitions, visibleIds);

  const hiddenTiles = allowedIds
    .filter((id) => !visibleIds.includes(id))
    .map((id) => ({
      id,
      label: i18n.t(prop(id, DASHBOARD_TILE_CATALOGUE).labelKey),
      previewSize:
        activeArrangement.find((placement) => placement.id === id)?.size ??
        prop(id, DASHBOARD_TILE_CATALOGUE).defaultSize,
    }));

  const isCustomising = isNotNil(draftArrangement);

  const updateDraft = (
    update: (placements: DashboardTilePlacement[]) => DashboardTilePlacement[]
  ) => {
    setDraftArrangement((current) => (isNil(current) ? null : update(current)));
  };

  const startCustomising = () => setDraftArrangement(savedArrangement);

  const cancelCustomising = () => setDraftArrangement(null);

  const resetArrangement = () =>
    setDraftArrangement(
      reflowDashboardGrid(withDefaultSizes(buildDefaultDashboardArrangement()), allowedIds)
    );

  // Compact the whole board in its current visual order, not the arrangement's record order.
  const hideTile = (id: DashboardTileId) =>
    updateDraft((placements) =>
      isCustomisationEnabled && allowedIds.includes(id)
        ? hideDashboardTile(placements, id, getVisibleIds(placements))
        : placements
    );

  // Keep every incumbent fixed; restore into a free stored rectangle or the first fitting gap.
  const showTile = (id: DashboardTileId) =>
    updateDraft((placements) =>
      isCustomisationEnabled && allowedIds.includes(id)
        ? showDashboardTile(placements, id, getVisibleIds(placements))
        : placements
    );

  // The endpoint takes the whole arrangement, so a second save started before the first settles
  // could land out of order and overwrite the newer one.
  const saveArrangement = () => {
    if (isNil(draftArrangement) || isSaving) {
      return;
    }

    const positionedArrangement = positionVisibleDashboardTiles(draftArrangement, visibleIds);
    putUserDashboardArrangement({
      gridColumns: DASHBOARD_GRID_COLUMNS,
      tiles: positionedArrangement.map((placement) => ({
        ...placement,
        size: toDashboardTileSizeCode(placement.size),
      })),
    })
      .unwrap()
      .catch(handleApiError);
    setDraftArrangement(null);
  };

  const applyDrop = (transaction: DashboardDropTransaction) =>
    updateDraft((placements) => reorderDashboardGrid(placements, transaction, visibleIds));

  const resizeTile = (id: DashboardTileId, size: DashboardTileSize) =>
    updateDraft((placements) => setDashboardTileSize(placements, id, size, visibleIds));

  return {
    isCustomising,
    isSaving,
    shownTiles,
    startCustomising,
    cancelCustomising,
    saveArrangement,
    resetArrangement,
    applyDrop,
    resizeTile,
    hideTile,
    showTile,
    hiddenTiles,
    allowedTileCount: allowedIds.length,
  };
}
